import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIsAuthenticated } from '@azure/msal-react';
import { createPortal } from 'react-dom';

import { startup, startupFinished } from './actions';
import SessionRouter from './components/navigation/SessionRouter';
import PublicRouter from './components/navigation/PublicRouter';
import { ImpersonateSession } from './components/common/ImpersonateSession';
import FigoLoadingOverlay from './components/common/FigoLoadingOverlay';
import GenericError from './components/genericError/GenericError';
import { DownBanner } from './components/downBanner';

import { useB2CInfo, useContactUsToggler } from './hooks';
import { doNothing } from './services/utils';
import app from './app.json';
import { useDownBanner } from './components/downBanner/hooks';
import { ContactUsModal } from './components/sideMenu/contactUs/ContactUsModal';
import { CONTACT_US_STEPS } from './components/sideMenu/contactUs/contactUs.constants';

const VersionData = () => createPortal(
  <meta data-version={app.version} name="App version" />,
  document.head,
);

const AppRouter = () => {
  const dispatch = useDispatch();

  const pathname = window.location.pathname.split('/')[1];
  const store = useSelector(({
    policies,
    session,
  }) => ({ policies, session }), shallowEqual);
  const { logoutB2C, getAllTokens } = useB2CInfo();
  const [allTokensLoaded, setAllTokensLoaded] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  const {
    globalContactUsModalVisible,
    onContactUsToggle,
  } = useContactUsToggler();

  useEffect(() => {
    switch (pathname) {
      case 'login':
        logoutB2C();
        break;
      case 'paymentMethod':
        localStorage.clear();
        dispatch(startupFinished());
        break;
      default:
        doNothing();
    }

    return () => null;
  }, []);

  const getTokensBeforeStartUp = async () => {
    await getAllTokens();
    dispatch(startup());
    setAllTokensLoaded(true);
  };

  const hasSession = useMemo(() => (
    Boolean(isAuthenticated
      && store.session.privateToken
      && store.session.userInfo)
  ), [isAuthenticated, store.session.privateToken, store.session.userInfo]);

  useEffect(() => {
    if (isAuthenticated) {
      getTokensBeforeStartUp();
    }
  }, [isAuthenticated]);

  const renderPageContent = () => {
    if (isAuthenticated) {
      if (store.session.startupFailed) {
        return <GenericError />;
      }

      if (hasSession && allTokensLoaded) {
        return <SessionRouter />;
      }

      // waiting for session
      return <FigoLoadingOverlay visible />;
    }

    return <PublicRouter />;
  };

  const {
    downBannerVisible,
    bannerContent,
    downBannerLoading,
  } = useDownBanner();

  if (downBannerLoading) {
    return <FigoLoadingOverlay visible />;
  }

  if (downBannerVisible) {
    return <DownBanner bannerContent={bannerContent} />;
  }

  return (
    <Router>
      {renderPageContent()}

      <ImpersonateSession />

      <VersionData />

      <ContactUsModal
        companyStep={CONTACT_US_STEPS.HOME}
        onClose={onContactUsToggle}
        show={globalContactUsModalVisible}
      />
    </Router>
  );
};

export default AppRouter;
